import type { ViewTheme } from '@/models/assessmentViews'

/**
 * prettifyRequirementCode (IR-116, Opus review Blocker 2 — Aymeric 2026-08-13:
 * "keep the rules simple, no acronym dictionary") — raw requirement keys never
 * reach the user's eyes. QMS anchors already carry printable codes
 * («Art. 10(9)(m)», «8.2.4 (follow-up)»); the raw look is TD families with no
 * clause number, where the code falls back to `family.Rn` / `family.presence`.
 *
 * SHAPE rules only (no dictionary — acronyms come out sentence-cased, e.g.
 * «Pmcf», an accepted trade-off for zero maintenance):
 *  - already-human codes (spaces, parens, uppercase families, plain clause
 *    numbers) pass through untouched;
 *  - `7_5_6`               → «7.5.6» (underscore clause);
 *  - `4.4.changes`         → «4.4 (changes)» (the plan's own code form);
 *  - `family_tokens.Item`  → «Family tokens – Item» («R10» kept, `presence`
 *    → «Presence»);
 *  - `family_tokens_r10`   → «Family tokens – R10» (the UID form — see below).
 * Callers keep the RAW key in the tooltip.
 *
 * ## Why the underscore counter form exists (MYAC-1172 QA)
 *
 * A requirement has two keys that differ only in one separator: the printable
 * CODE (`declaration_of_conformity.r6`) and the UID
 * (`declaration_of_conformity_r6`). The platform's own grammar is explicit —
 * `_uid_family()` in the backend strips `_r\d+[a-z]?` to get the family, and
 * documents it as "pure convention over the platform key grammar".
 *
 * Only the dotted form was handled here, so every surface holding a UID rather
 * than a code printed the raw key. That was invisible while those surfaces were
 * read-only, and stopped being invisible the moment Cohérence's chips became
 * worth clicking: 15 of 20 read `declaration_of_conformity_r6` while the dialog
 * they opened titled the same requirement «Declaration of conformity – R6».
 * One requirement, two names, decided by which key the surface happened to hold.
 */

/** snake_case family + one item after the last dot — the TD fallback shape. */
const RAW_FAMILY_ITEM = /^[a-z][a-z0-9_]*\.[A-Za-z0-9_]+$/
/** The UID shape: the same family, joined to its item counter by an underscore. */
const RAW_FAMILY_COUNTER = /^([a-z][a-z0-9_]*?)_(r\d+[a-z]?)$/
/** clause digits (dot- or underscore-joined) + one word suffix. */
const NUMERIC_WITH_WORD = /^(\d+(?:[._]\d+)*)[._]([a-z][a-z0-9_]*)$/
/** clause digits only, underscore-joined ok. */
const NUMERIC_ONLY = /^\d+(?:[._]\d+)*$/

/** «family tokens» + «Item» → «Family tokens – Item». The one presentation,
 * so the dotted code and the underscored uid cannot render differently. */
function familyItem(family: string, item: string): string {
  const words = family.replace(/_/g, ' ')
  const shown = /^r\d+[a-z]?$/i.test(item)
    ? item.toUpperCase()
    : item.charAt(0).toUpperCase() + item.slice(1).replace(/_/g, ' ')
  return `${words.charAt(0).toUpperCase()}${words.slice(1)} – ${shown}`
}

export function prettifyRequirementCode(code: string): string {
  if (!code) return code
  if (NUMERIC_ONLY.test(code)) return code.replace(/_/g, '.')
  const numWord = NUMERIC_WITH_WORD.exec(code)
  if (numWord) return `${numWord[1].replace(/_/g, '.')} (${numWord[2].replace(/_/g, ' ')})`
  if (RAW_FAMILY_ITEM.test(code)) {
    const at = code.lastIndexOf('.')
    return familyItem(code.slice(0, at), code.slice(at + 1))
  }
  // The uid form. Checked AFTER the numeric rules on purpose: `4_4_r2` is a
  // clause with a word suffix, not a family with a counter, and the rule that
  // matches first is the one that describes it.
  const counter = RAW_FAMILY_COUNTER.exec(code)
  if (counter) return familyItem(counter[1], counter[2])
  return code
}

/**
 * Does this reference code, AS IT WOULD BE PRINTED, read as a database key?
 *
 * `RefChip` prints whatever the projection sends, so a real TD project put
 * `obligation:pms_plan`, `obligation:pmcf` and `gmdn_code` on screen as clause
 * references beside properly-named siblings (design review 2026-09-04,
 * blocker). The repo's rule is absolute: a server-sent identifier never renders
 * to a customer.
 *
 * SHAPE rules only, and deliberately narrow — the same trade-off
 * `prettifyRequirementCode` made, for the same reason (no dictionary to
 * maintain, and a real clause code must never be swallowed):
 *
 *  - a PLATFORM NAMESPACE prefix — a lowercase snake_case word followed by
 *    `:` (`obligation:pms_plan`, `iso_13485:7.3.9`, `qms:_corpus`), the key
 *    grammar every uid in this system obeys;
 *  - `_`-joined lowercase words with no other punctuation (`gmdn_code`,
 *    `pms_plan`) — snake_case, which no printed clause reference uses.
 *
 * Everything else passes: `7.3.9`, `Art. 10(10)`, `C6.2`, `Annex II`, `R4`,
 * `IEC 62304 §5.1`, and the `Design controls – R2` form
 * `prettifyRequirementCode` already produces.
 *
 * The namespace is matched, rather than a bare `:` anywhere, because a colon
 * is ALSO how a printed standard carries its edition — `ISO 13485:2016 §7.3.9`
 * is a real client-facing code and the wider rule swallowed it (review I-2).
 *
 * Run on the PRETTIFIED string, not the raw code: `design_controls_r2` becomes
 * a readable name before it is judged, and only what actually survives to the
 * screen is guarded.
 */
const SNAKE_CASE_KEY = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/
/** The platform's key grammar: `namespace:rest`, namespace in snake_case. */
const NAMESPACED_KEY = /^[a-z][a-z0-9_]*:/

export function looksLikeIdentifier(shownCode: string): boolean {
  const shown = shownCode.trim()
  if (!shown) return false
  return NAMESPACED_KEY.test(shown) || SNAKE_CASE_KEY.test(shown)
}

/**
 * The obligation namespace — the one uid prefix whose noun the client knows
 * («Obligation»), as opposed to a clause of a referential.
 *
 * `derive_periodic_tasks` mints `obligation:{family}` as a SYNTHETIC anchor for
 * a periodic obligation that no requirement governs. No `assessment_items` row
 * carries such a uid, which is why the callers also treat it as unopenable
 * rather than merely unnamed.
 */
export const OBLIGATION_PREFIX = 'obligation:'

/** Is this reference a synthetic obligation anchor rather than a requirement? */
export function isObligationRef(raw: string): boolean {
  return (raw ?? '').trim().startsWith(OBLIGATION_PREFIX)
}

/**
 * A requirement code or uid AS THE CLIENT MAY READ IT — `''` when nothing in it
 * is printable.
 *
 * Two steps, in this order:
 *
 *  1. `prettifyRequirementCode`, then the guard above: what survives is a real
 *     code (`7.3.9`, `Art. 10(10)`, `Design controls – R2`) and is returned.
 *  2. A namespaced uid carries its clause AFTER the namespace —
 *     `iso_13485:7.3.9` IS the clause the reader was scanning for, wearing a
 *     database prefix. The tail is prettified and guarded in turn, and it must
 *     LOOK like a printed reference: it starts on a letter or a digit and it
 *     numbers something. So `iso_13485:7.3.9` → `7.3.9`, while
 *     `obligation:pms_plan` (snake), `obligation:pmcf` (a bare word numbering
 *     nothing) and `qms:_corpus` (no printable first character) all yield
 *     nothing.
 *
 * `''` is the caller's cue to name the KIND of thing instead — never to print
 * the key (design review 2026-09-04, blocker; review C-1/I-1/I-2).
 *
 * One exception to the "numbers something" test (review round 1, M-1):
 * `eu_mdr:annex_ii` numbers nothing in ARABIC digits, but "Annex II" is a real
 * client-facing reference, not a leak. Narrow on purpose — only the `annex_`
 * family, only pure roman-numeral tails — the same no-dictionary trade-off
 * `prettifyRequirementCode` already makes.
 */
export function printableRequirementCode(raw: string): string {
  const shown = prettifyRequirementCode((raw ?? '').trim())
  if (!shown) return ''
  if (!looksLikeIdentifier(shown)) return shown
  const at = shown.indexOf(':')
  if (at < 0) return ''
  const rawTail = shown.slice(at + 1)
  const annex = /^annex_([ivxlcdm]+)$/i.exec(rawTail)
  if (annex) return `Annex ${annex[1].toUpperCase()}`
  const tail = prettifyRequirementCode(rawTail)
  const printable = /^[A-Za-z0-9]/.test(tail) && /\d/.test(tail) && !looksLikeIdentifier(tail)
  return printable ? tail : ''
}

/**
 * The words of ANY reference chip: the code when it is readable, and otherwise
 * the NOUN of the thing referenced — never the key.
 *
 * The noun is decided by the reference's own namespace: `obligation:…` is an
 * obligation, anything else is a requirement of a referential. A neutral
 * category is the point — a single «Obligation» named an ISO 13485 clause and
 * a GMDN device field as regulatory obligations, which is a wrong noun rather
 * than an unknown one (review I-2).
 *
 * Shared by the Playbook's rejection chips, the action plan's origin chip and
 * a task's source-requirement chips, because they had the SAME leak and fixing
 * it in one place is what keeps the three surfaces naming one reference one
 * way (design review 2026-09-04, F2).
 *
 * Pass `code || uid`: the code is what the client reads, the uid is the
 * fallback name for a reference that has none of its own — the same order
 * `RequirementDetail` uses (`req.code || req.requirement_uid`).
 */
export function requirementRefLabel(raw: string, t: (key: string) => string): string {
  const trimmed = (raw ?? '').trim()
  return (
    printableRequirementCode(trimmed) ||
    t(isObligationRef(trimmed) ? 'shared.reference_obligation' : 'shared.unnamed_requirement')
  )
}

/**
 * A task's `category` AS THE CLIENT MAY READ IT — `''` when it carries a key
 * (MYAC-1629, MYAC-1614).
 *
 * The backend writes `category` once, at acceptance, and an ungrouped row once
 * wrote it from a key: the bare `periodic:{family}` topic, or the first
 * anchor's key-shaped code glued to its title (`continuity_pms_plan.presence
 * PMS plan continuity evidence…`). The backend no longer does; tasks accepted
 * before keep the value, so the chip is judged here too.
 *
 * Three cases, the first two from the shape rules above: the whole value is a
 * key, its first word is the TD `family.item` code, or it is only a 4C facet
 * value (`consistency`), an enum the backend copies into a row's topic. Each
 * leaves nothing to print, so no chip — the same answer the backend gives now.
 */
const THEME_VALUES: ReadonlySet<string> = new Set<ViewTheme | 'unknown'>([
  'compliance',
  'completeness',
  'continuity',
  'consistency',
  'unknown',
])

export function printableCategory(raw: string | null | undefined): string {
  const shown = (raw ?? '').trim()
  if (!shown || looksLikeIdentifier(shown) || THEME_VALUES.has(shown)) return ''
  return RAW_FAMILY_ITEM.test(shown.split(/\s/, 1)[0]) ? '' : shown
}
